<template>
  <div
    id="cart"
    class="container-fluid">
    <div class="row mt-3">
      <!-- Cart Items -->
      <CartSummary
        class="col"
        @remove-registration="removeReg"
      />
      <!-- / Cart Items -->
    </div>
    <div class="row border-bottom border-muted py-3">
      <div class="
        col
        d-flex
        flex-column
        flex-lg-row
        align-items-center
      ">
        <h3 class="mb-3 m-lg-0">
          Cart Total: {{registrationSession.amount_to_charge | currency}}
        </h3>

        <div class="flex-fill d-xs-none d-lg-block"></div>

        <div class="d-flex flex-column flex-lg-row justify-content-between">
          <b-button
            class="mr-lg-3"
            variant="primary"
            type="button"
            :disabled="submitting"
            @click="goToEvent()">
            Add {{!cartEmpty ? 'Another' : ''}} Registration
          </b-button>

          <!-- Payment Request Button (Apple Pay / Google Pay) -->
          <button
            id="stripe-payment-request-mount"
            class="stripe-payment-request mt-3 mt-lg-0 mr-lg-3"
            v-if="loggedInUser.id && paymentRequestStatus !== null && !isZeroDollarCart && !cartEmpty"
            type="button"
            :disabled="cartEmpty || failedRegistration || submitting">
          </button>
          <!-- / Payment Request Button -->

          <!-- Partial Payment -->
          <b-button
            v-if="registrationsAllowingPartialPayments.length > 0 && !isZeroDollarCart && !cartEmpty"
            class="mt-3 mt-lg-0 mr-lg-3"
            variant="success"
            type="button"
            :disabled="cartEmpty || failedRegistration || submitting"
            @click="showPartialPaymentModal">
            Make Partial Payment{{registrationsAllowingPartialPayments.length > 1 ? 's' : ''}}
          </b-button>
          <!-- / Partial Payment -->

          <b-button
            class="mt-3 mt-lg-0"
            variant="success"
            type="button"
            :disabled="cartEmpty || failedRegistration || submitting || !loggedInUser.id"
            @click="checkoutClick">
            <span v-if="isZeroDollarCart && !cartEmpty">Complete Registration Without Charge</span>
            <span v-else>Checkout</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Partial Payments Modal -->
    <b-modal
      id="partial-payments-modal"
      ref="partialPaymentsModal"
      title="Partial Payments"
      hide-footer
      size="lg"
      @shown="mountPartialPaymentPaymentRequestButton"
      @hide="checkDirtyModalForm($event, isPartialPaymentsDirty)"
      @hidden="resetPartialPaymentsForm(); unmountPartialPaymentPaymentRequestButton()"
      v-model="partialPaymentsModalOpen">
      <b-form
        id="partial-payments-form"
        name="partialPaymentsForm"
        @submit.prevent="partialPaymentsCheckoutStart">
        <!-- Repeat Over ALL Registrations -->
        <div
          v-for="registration in orderedRegistrations"
          :key="registration.id"
          class="row mb-3">

          <!-- Registration -->
          <div class="col-lg">
            <label :for="`partial-payments-${registration.id}`">
              {{registration.event.name}} - {{registration.registrant.full_name}}
              <small class="text-danger">
                <div v-if="registration.event.partial_payments_open && registration.event.registration_open">
                  Enter an amount between
                  {{registration.cost.calc_minimum_payment | currency}}
                  and
                  {{registration.cost.calc_amount_total | currency}}.
                </div>
                <div v-else-if="!registration.event.registration_open">
                  Waitlist - {{registration.cost.calc_amount_total | currency}} Payment Due Upon Arrival.
                </div>
                <div v-else>Partial Payment not accepted for this event.</div>
              </small>
            </label>
          </div>
          <!-- / Registration -->

          <!-- Payment -->
          <div class="col-lg-auto partial-payment-entry">
            <Number
              v-if="registration.event.partial_payments_open && registration.event.registration_open"
              class="enter-payment"
              placeholder="0.00"
              type="currency"
              :id="`partial-payments-${registration.id}`"
              :name="`partialPayments${registration.id}`"
              :min="registration.cost.calc_minimum_payment"
              :max="registration.cost.calc_amount_total"
              :disabled="!!partialPaymentsLoadingMsg"
              v-model="partialPayments[registration.id]"
            />

            <Number
              v-else
              class="display-payment"
              :id="`partial-payments-${registration.id}`"
              :name="`partialPayments${registration.id}`"
              type="currency"
              :value="registration.cost.amount_to_charge"
              disabled
            />
          </div>
          <!-- / Payment -->
        </div>
        <!-- / Repeat Over ALL Registrations -->

        <hr>

        <!-- Total -->
        <div class="d-flex align-items-center justify-content-end">
          <label class="m-0 mr-2 mt-1">
            Total:
          </label>
          <div class="partial-payment-entry">
            <Number
              class="display-payment"
              type="currency"
              :value="totalWithPartialPayments"
              disabled
            />
          </div>
        </div>
        <!-- / Total -->

        <!-- Form Buttons -->
        <div class="row justify-content-end mt-4">
          <!-- Payment Request Button -->
          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <button
              id="stripe-payment-request-pp-mount"
              class="stripe-payment-request w-100 w-lg-auto"
              type="button"
              :disabled="!!partialPaymentsLoadingMsg || totalWithPartialPayments < minimumTotalPayment || submitting">
            </button>
          </div>

          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <b-button
              type="submit"
              class="w-100 w-lg-auto"
              variant="success"
              :disabled="!!partialPaymentsLoadingMsg || totalWithPartialPayments < minimumTotalPayment || submitting">
              Pay With Credit Card
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              type="button"
              variant="danger"
              :disabled="!!partialPaymentsLoadingMsg || submitting"
              @click="partialPaymentsModalOpen = false;">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="partial-payments-loading"
        variant="light"
        :msg="partialPaymentsLoadingMsg">
      </Loader>
    </b-modal>
    <!-- / Partial Payments Modal -->

    <!-- Stripe Checkout -->
    <vue-stripe-checkout
      ref="checkoutRef"
      image="https://stripe.com/img/documentation/checkout/marketplace.png"
      name="KJK Registrations"
      :description="`${event.org_name}: ${event.name}`"
      :allow-remember-me="false"
      :amount="checkoutOptions.amount"
      @done="checkoutOptions.done"
      @canceled="checkoutOptions.canceled">
    </vue-stripe-checkout>
    <!-- / Stripe Checkout -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import AppMixin from '../services/mixin.app';
import CartSummary from '../components/CartSummary.vue';
import Number from '../components/Number.vue';
import DirtyForm from '../services/dirty-form';

// eslint-disable-next-line
let stripe;
let elements;
let paymentRequest;
let prButton;

export default {
  name: 'cart',
  mixins: [
    AppMixin,
    DirtyForm,
  ],
  components: {
    CartSummary,
    Number,
  },
  data() {
    return {
      submitting: false,
      paymentRequestStatus: null,
      paymentRequestButtonMounted: false,
      paymentRequestButtonMountedPartialPayments: false,
      paymentProcessing: false,

      // partial payments
      partialPaymentsModalOpen: false,
      partialPayments: {},
      partialPaymentsCopy: {},
      partialPaymentsLoadingMsg: '',

      // checkout
      checkoutOptions: {
        amount: 0,
        done: () => {},
        canceled: () => {},
      },
    };
  },
  watch: {
    costInCents(cost) {
      if (!this.partialPaymentsModalOpen) {
        this.updatePaymentRequestAmount(cost);
      }
    },
    partialPaymentsCostInCents(cost) {
      if (this.partialPaymentsModalOpen) {
        this.updatePaymentRequestAmount(cost);
      }
    },
  },
  computed: {
    costInCents() {
      return this.convertFloatDollarsToIntegerCents(this.registrationSession.amount_to_charge);
    },

    // partial payments
    orderedRegistrations() {
      return _.orderBy(
        this.registrationSession.registrations,
        r => r.event.partial_payments_open && r.event.registration_open,
        'desc',
      );
    },
    registrationsAllowingPartialPayments() {
      return _.filter(
        this.registrationSession.registrations,
        r => r.event.partial_payments_open && r.event.registration_open,
      );
    },
    isPartialPaymentsDirty() {
      const partialPaymentsJSON = JSON.stringify(this.partialPayments);
      const partialPaymentsCopyJSON = JSON.stringify(this.partialPaymentsCopy);
      return partialPaymentsJSON !== partialPaymentsCopyJSON;
    },
    totalWithPartialPayments() {
      let sum = 0;

      _.each(this.registrationSession.registrations, (r) => {
        let cost = this.convertFloatDollarsToIntegerCents(r.cost.amount_to_charge);

        if (r.event.partial_payments_open) {
          if (isNaN(this.partialPayments[r.id])) {
            cost = this.convertFloatDollarsToIntegerCents(r.cost.calc_minimum_payment);
          } else {
            cost = this.convertFloatDollarsToIntegerCents((this.partialPayments[r.id]));
          }
        }

        sum += cost;
      });

      return sum / 100;
    },
    partialPaymentsCostInCents() {
      return this.convertFloatDollarsToIntegerCents(this.totalWithPartialPayments);
    },
    minimumTotalPayment() {
      let sum = 0;

      _.each(this.registrationSession.registrations, (r) => {
        let cost = this.convertFloatDollarsToIntegerCents((r.cost.amount_to_charge));

        if (r.event.partial_payments_open) {
          cost = this.convertFloatDollarsToIntegerCents(r.cost.calc_minimum_payment);
        }

        sum += cost;
      });

      return sum / 100;
    },
  },
  methods: {
    checkoutClick() {
      this.submitting = true;
      this.showGlobalLoader('Processing...');

      this.checkCostScenario(() => {
        if (this.isZeroDollarCart) {
          return this.confirmZeroDollarCharge();
        }
        return this.checkoutStart();
      })
        .finally(() => {
          this.submitting = false;
          this.hideGlobalLoader();
        });
    },
    completeCharge(token, partialPayments) {
      const { uuid } = this.registrationSession;
      return DataSvc.createCharge(uuid, token, partialPayments)
        .then(() => {
          this.updateActiveRegistrationSession();
          return this.updateClosedRegistrationSessions();
        })
        .then(() => {
          this.$router.push({
            name: 'confirmation',
            params: { uuid },
          });
        });
    },
    checkoutStart() {
      this.checkoutOptions = {
        amount: this.costInCents,
        done: this.checkoutDone,
        canceled: this.hideGlobalLoader,
      };

      this.showGlobalLoader(true);
      this.$nextTick(this.$refs.checkoutRef.open);
      return Promise.resolve();
    },
    checkoutDone({ token }) {
      this.showGlobalLoader('Processing payment...');
      this.completeCharge(token.id)
        .finally(this.hideGlobalLoader)
        .catch(() => {}); // server error message already shown by axios interceptor
    },
    confirmZeroDollarCharge() {
      this.showGlobalLoader('Processing...');
      return this.completeCharge()
        .catch(() => {}); // server error message already shown by axios interceptor
    },
    resetPaymentRequestButton() {
      if (this.prButton && typeof this.prButton.unmount === 'function') {
        this.prButton.unmount();
      }

      this.paymentRequestStatus = null;
    },
    removeReg(id) {
      return this.removeRegistration(id)
        .then(() => {
          if (this.cartEmpty) {
            this.resetPaymentRequestButton();
            return;
          }

          this.updatePaymentRequestAmount(this.costInCents);
        });
    },
    updatePaymentRequestAmount(costInCents) {
      if (this.paymentRequestStatus !== null
        && !this.paymentProcessing
        && !this.cartEmpty) {
        paymentRequest.update({
          total: {
            label: 'KJK Registrations',
            amount: costInCents,
          },
        });
      }
    },

    // partial payments
    showPartialPaymentModal() {
      this.submitting = true;
      this.showGlobalLoader('Processing...');

      this.checkCostScenario(() => {
        this.partialPaymentsModalOpen = true;

        _.each(this.registrationSession.registrations, (r) => {
          if (r.event.partial_payments_open) {
            this.$set(this.partialPayments, r.id, r.cost.calc_minimum_payment);
            this.$set(this.partialPaymentsCopy, r.id, r.cost.calc_minimum_payment);
          }
        });

        this.updatePaymentRequestAmount(this.partialPaymentsCostInCents);
      }).finally(() => {
        this.submitting = false;
        this.hideGlobalLoader();
      });
    },
    resetPartialPaymentsForm() {
      this.partialPayments = {};
      this.partialPaymentsCopy = {};

      this.updatePaymentRequestAmount(this.costInCents);
    },
    partialPaymentsCheckoutStart() {
      this.submitting = true;

      this.checkCostScenario(() => {
        this.checkoutOptions = {
          amount: this.partialPaymentsCostInCents,
          done: this.partialPaymentsCheckoutDone,
          canceled: this.partialPaymentsCheckoutCanceled,
        };

        this.partialPaymentsLoadingMsg = true;
        this.$nextTick(this.$refs.checkoutRef.open);
      }).finally(() => {
        this.submitting = false;
      });
    },
    partialPaymentsCheckoutDone({ token }) {
      this.partialPaymentsLoadingMsg = 'Processing payment...';
      this.completeCharge(token.id, this.partialPayments)
        .then(() => {
          this.partialPaymentsModalOpen = false;
        })
        .finally(() => {
          this.partialPaymentsLoadingMsg = '';
        })
        .catch(() => {}); // server error message already shown by axios interceptor
    },
    partialPaymentsCheckoutCanceled() {
      this.partialPaymentsLoadingMsg = '';
    },
    mountPartialPaymentPaymentRequestButton() {
      if (prButton
        && this.paymentRequestStatus !== null
        && !this.paymentRequestButtonMountedPartialPayments) {
        if (this.paymentRequestButtonMounted) {
          prButton.unmount();
          this.paymentRequestButtonMounted = false;
        }

        prButton.mount('#stripe-payment-request-pp-mount');
        this.paymentRequestButtonMountedPartialPayments = true;
      }
    },
    unmountPartialPaymentPaymentRequestButton() {
      if (prButton
        && this.paymentRequestStatus !== null
        && this.paymentRequestButtonMountedPartialPayments) {
        prButton.unmount();
        this.paymentRequestButtonMountedPartialPayments = false;

        prButton.mount('#stripe-payment-request-mount');
        this.paymentRequestButtonMounted = true;
      }
    },
  },
  destroyed() {
    if (prButton) {
      prButton.unmount();
      prButton.destroy();
    }
  },
  created() {
    const initCart = () => {
      let validateCostPromise = Promise.resolve();

      // eslint-disable-next-line
      stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_API_KEY);
      elements = stripe.elements();

      paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'KJK Registrations',
          amount: this.costInCents,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      paymentRequest.canMakePayment()
        .then((status) => {
          this.paymentRequestStatus = status;

          if (status !== null) {
            paymentRequest.on('token', (e) => {
              this.partialPaymentsModalOpen = false;
              this.paymentProcessing = true;
              this.showGlobalLoader('Processing payment...');

              validateCostPromise
                .then(() => this.completeCharge(e.token.id, this.partialPayments), () => e.complete('fail'))
                .then(() => e.complete('success'), () => e.complete('fail'))
                .finally(() => {
                  this.hideGlobalLoader();
                  this.paymentProcessing = false;
                });
            });

            paymentRequest.on('cancel', () => {
              this.paymentProcessing = false;
              this.partialPaymentsLoadingMsg = false;
              this.hideGlobalLoader();
            });

            try {
              prButton = elements.create('paymentRequestButton', {
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    height: '38px',
                  },
                },
              });
            } catch (e) {
              if (e.message !== 'Can only create one Element of type paymentRequestButton.') {
                // eslint-disable-next-line
                console.error(e);
              }
            }

            if (!prButton) return;

            prButton.on('click', () => {
              if (this.partialPaymentsModalOpen) {
                this.partialPaymentsLoadingMsg = true;
              } else {
                this.showGlobalLoader(true);
              }

              validateCostPromise = this.checkCostScenario();
            });

            this.$nextTick(() => {
              if (!this.cartEmpty && !this.isZeroDollarCart) {
                prButton.mount('#stripe-payment-request-mount');
                this.paymentRequestButtonMounted = true;
              }
            });
          }
        });
    };

    this.initPromise.then(initCart);
  },
};
</script>

<style lang="scss"></style>
